import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { createSelector } from "reselect";

const initialState = {
  botTrades: {}, // Store trades per bot ID
  loading: 'idle',
  error: null,
  isConnected: false,
  subscribedBots: [] // Changed from Set to Array for serialization
};

// Thunk action creators for socket operations
export const initializeSocketAsync = createAsyncThunk(
  'socketTrades/initializeSocketAsync',
  async () => {
    if (typeof window !== 'undefined') {
      try {
        const { default: socketService } = await import('../../utils/socketService');
        socketService.connect();
        return { isConnected: socketService.getConnectionStatus().isConnected };
      } catch (error) {
        console.error('Failed to initialize socket service:', error);
        throw error;
      }
    }
    return { isConnected: false };
  }
);

export const subscribeToBotAsync = createAsyncThunk(
  'socketTrades/subscribeToBotAsync',
  async (botId) => {
    if (typeof window !== 'undefined') {
      try {
        const { default: socketService } = await import('../../utils/socketService');
        socketService.subscribeToBot(botId);
        return botId;
      } catch (error) {
        console.error('Failed to subscribe to bot:', error);
        throw error;
      }
    }
    return botId;
  }
);

export const unsubscribeFromBotAsync = createAsyncThunk(
  'socketTrades/unsubscribeFromBotAsync',
  async (botId) => {
    if (typeof window !== 'undefined') {
      try {
        const { default: socketService } = await import('../../utils/socketService');
        socketService.unsubscribeFromBot(botId);
        return botId;
      } catch (error) {
        console.error('Failed to unsubscribe from bot:', error);
        throw error;
      }
    }
    return botId;
  }
);

const socketTradesSlice = createSlice({
  name: 'socketTrades',
  initialState,
  reducers: {
    // Initialize socket connection
    initializeSocket(state) {
      // Only update the state, don't call socket service here
      state.loading = 'pending';
    },

    // Update connection status
    updateConnectionStatus(state, action) {
      state.isConnected = action.payload.isConnected;
      state.loading = 'idle';
    },

    // Subscribe to bot trades
    subscribeToBotTrades(state, action) {
      const botId = action.payload;
      if (!state.subscribedBots.includes(botId)) {
        state.subscribedBots.push(botId);
      }
    },

    // Subscribe to multiple bot trades
    subscribeToMultipleBotTrades(state, action) {
      const botIds = action.payload;
      botIds.forEach(botId => {
        if (!state.subscribedBots.includes(botId)) {
          state.subscribedBots.push(botId);
        }
      });
    },

    // Unsubscribe from bot trades
    unsubscribeFromBotTrades(state, action) {
      const botId = action.payload;
      state.subscribedBots = state.subscribedBots.filter(id => id !== botId);
    },

    // Unsubscribe from all bot trades
    unsubscribeFromAllBotTrades(state) {
      state.subscribedBots = [];
    },

    // Update trades for a specific bot - optimized to prevent unnecessary updates
    updateBotTrades(state, action) {
      const { botId, trades, count } = action.payload;
      
      if (!state.botTrades[botId]) {
        state.botTrades[botId] = {
          trades: [],
          loading: 'idle',
          error: null,
          count: 0,
          nextTradeAt: null,
          nextTradeInMs: null
        };
      }
      
      const current = state.botTrades[botId];
      // Quick check: compare count first (cheaper), then length, then update
      // Always update if count changed or trades array reference changed
      // This prevents expensive deep comparisons while ensuring updates happen
      const shouldUpdate = current.count !== count || 
                          current.trades.length !== trades.length ||
                          current.trades !== trades;
      
      if (shouldUpdate) {
        state.botTrades[botId] = {
          ...current,
          trades,
          count,
          loading: 'idle',
          error: null
        };
      }
    },

    // Update bot status - optimized to prevent unnecessary updates
    updateBotStatus(state, action) {
      const { botId, status } = action.payload;
      
      if (!state.botTrades[botId]) {
        state.botTrades[botId] = {
          trades: [],
          loading: 'idle',
          error: null,
          count: 0,
          status: null
        };
      }
      
      // Only update if status actually changed
      if (state.botTrades[botId].status !== status) {
        state.botTrades[botId] = {
          ...state.botTrades[botId],
          status
        };
      }
    },

    // Update next trade info - optimized to prevent unnecessary re-renders
    updateNextTradeInfo(state, action) {
      const { botId, nextTradeAt, nextTradeInMs } = action.payload;
      if (!state.botTrades[botId]) {
        state.botTrades[botId] = {
          trades: [],
          loading: 'idle',
          error: null,
          count: 0,
          nextTradeAt: null,
          nextTradeInMs: null
        };
      }
      // Only update if values actually changed to prevent unnecessary re-renders
      const current = state.botTrades[botId];
      if (current.nextTradeAt !== nextTradeAt || current.nextTradeInMs !== nextTradeInMs) {
        state.botTrades[botId] = {
          ...current,
          nextTradeAt,
          nextTradeInMs
        };
      }
    },

    // Set loading state for a bot
    setBotTradesLoading(state, action) {
      const { botId, loading } = action.payload;
      
      if (!state.botTrades[botId]) {
        state.botTrades[botId] = {
          trades: [],
          loading: 'idle',
          error: null,
          count: 0
        };
      }
      
      state.botTrades[botId].loading = loading;
    },

    // Set error for a bot
    setBotTradesError(state, action) {
      const { botId, error } = action.payload;
      
      if (!state.botTrades[botId]) {
        state.botTrades[botId] = {
          trades: [],
          loading: 'idle',
          error: null,
          count: 0
        };
      }
      
      state.botTrades[botId].error = error;
      state.botTrades[botId].loading = 'idle';
    },

    // Clear trades for a specific bot
    clearBotTrades(state, action) {
      const botId = action.payload;
      if (state.botTrades[botId]) {
        delete state.botTrades[botId];
      }
    },

    // Reset all socket trades
    resetSocketTrades(state) {
      state.botTrades = {};
      state.loading = 'idle';
      state.error = null;
    },

    // Disconnect socket
    disconnectSocket(state) {
      state.isConnected = false;
      state.subscribedBots = [];
      state.loading = 'idle';
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeSocketAsync.pending, (state) => {
        state.loading = 'pending';
      })
      .addCase(initializeSocketAsync.fulfilled, (state, action) => {
        state.loading = 'idle';
        state.isConnected = action.payload.isConnected;
      })
      .addCase(initializeSocketAsync.rejected, (state, action) => {
        state.loading = 'idle';
        state.error = action.error.message;
      });
  }
});

export const {
  initializeSocket,
  updateConnectionStatus,
  subscribeToBotTrades,
  subscribeToMultipleBotTrades,
  unsubscribeFromBotTrades,
  unsubscribeFromAllBotTrades,
  updateBotTrades,
  updateBotStatus,
  updateNextTradeInfo,
  setBotTradesLoading,
  setBotTradesError,
  clearBotTrades,
  resetSocketTrades,
  disconnectSocket
} = socketTradesSlice.actions;

// Selectors - optimized with default values for nextTradeAt
export const selectSocketTrades = createSelector(
  [state => state.socketTrades.botTrades, (state, botId) => botId],
  (botTrades, botId) => botTrades[botId] || { 
    trades: [], 
    loading: 'idle', 
    error: null, 
    count: 0,
    nextTradeAt: null,
    nextTradeInMs: null
  }
);

export const selectSocketConnectionStatus = (state) => state.socketTrades.isConnected;
export const selectSubscribedBots = (state) => state.socketTrades.subscribedBots;

export default socketTradesSlice.reducer; 